import { useEffect, useMemo, useState, type ReactNode } from "react";
import { ArrowLeft, BarChart3, Check, Loader2, PenLine, Sparkles, Target, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ProductInfoCreative } from "@/feature/ads/cretives/types/creatives.types";
import { VIDEO_STYLES, type VideoStyle, type VideoGenerationRequest } from "../types/videoStudio.types";
import { VideoStudioGallery } from "./VideoStudioGallery";
import {
  buildVideoAngleOptions,
  VideoStudioSidebar,
  type ProductContextStatus,
  type VideoAngleOption,
} from "./VideoStudioSidebar";
import { PreviewApproval } from "./PreviewApproval";
import { AvatarWizardModal, type AvatarCommit } from "./AvatarWizardModal";
import useVideoGenerationStore from "@/store/videoGenerationStore";
import { useVideoDraftStore } from "@/store/videoDraftStore";
import { useVideoDraftFlag } from "../hooks/useVideoDraftFlag";
import { getVideoProductContext } from "@/services/ads/videos/videoDraftService";
import { AUDIO_MODE, type VideoProductContextResponse } from "@/services/ads/videos/videoDraft.types";

interface Step2VideoStudioProps {
  product: ProductInfoCreative;
  onClose: () => void;
  onStageChange?: (stage: StudioStage) => void;
}

type StudioStage = "style" | "angle" | "avatar" | "configure";

type SelectedAngle = {
  name: string;
  description: string;
};

const MODELING_STYLE_IDS = new Set(["product-modeling-voiceover", "product-modeling"]);
const PRIMARY_VIDEO_STYLE_ID = "product-modeling-voiceover";
const DEFAULT_VIDEO_STYLE = VIDEO_STYLES.find(
  (style) => style.enabled && style.id === PRIMARY_VIDEO_STYLE_ID
) ?? null;

function shortProductName(value?: string | null) {
  const normalized = (value ?? "").trim().replace(/\s+/g, " ");
  if (!normalized) return "";
  const primary = normalized.split(/\s[-–—|]\s|[,;]/)[0]?.trim() || normalized;
  if (primary.length <= 34) return primary;
  const cut = primary.slice(0, 34).trimEnd();
  const lastSpace = cut.lastIndexOf(" ");
  return lastSpace >= 16 ? cut.slice(0, lastSpace) : cut;
}

export function Step2VideoStudio({ product, onClose, onStageChange }: Step2VideoStudioProps) {
  const [selectedStyle, setSelectedStyle] = useState<VideoStyle | null>(DEFAULT_VIDEO_STYLE);
  const [stage, setStage] = useState<StudioStage>("style");
  const [isGenerating, setIsGenerating] = useState(false);
  // Phase 2 V2 — Avatar Wizard state. When `avatarWizardOpen` is true we
  // render the wizard in place of the studio panel (takeover UX, not a
  // popover) — the wizard is the user's only focus at that moment.
  const [avatarWizardOpen, setAvatarWizardOpen] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState<AvatarCommit | null>(null);
  const [selectedAngle, setSelectedAngle] = useState<SelectedAngle | null>(null);
  const [productContext, setProductContext] = useState<VideoProductContextResponse | null>(null);
  const [productContextStatus, setProductContextStatus] = useState<ProductContextStatus>("idle");
  const startVideoGeneration = useVideoGenerationStore((s) => s.startGeneration);
  const startDraft = useVideoDraftStore((s) => s.startDraft);
  const resetDraft = useVideoDraftStore((s) => s.reset);
  const storeStatus = useVideoDraftStore((s) => s.storeStatus);
  // Modal-owned draft states. Post-approve rendering lives in the gallery
  // in-flight store, so it must not hijack a new Create Video modal.
  const draftMode = [
    "directing",
    "ready",
    "approving",
    "rejecting",
    "editing_script",
    "regenerating_image",
    "regenerating_style",
    "generating_audio",
    "error",
    "done",
  ].includes(storeStatus);

  // Phase 2: feature flag — only enable draft preview for styles that have a
  // director_v1 agent created in agent-config (currently animated-problem).
  // For everything else we fall through to the legacy direct-render flow.
  const draftEnabled = useVideoDraftFlag(selectedStyle?.id);

  useEffect(() => {
    onStageChange?.(stage);
  }, [onStageChange, stage]);

  useEffect(() => {
    let cancelled = false;

    const loadContext = async () => {
      if (!product.product_id) {
        setProductContextStatus("fallback");
        return;
      }

      setProductContextStatus("loading");

      try {
        const response = await getVideoProductContext(product.product_id, product.website_id);
        if (cancelled) return;

        const context = response.res ?? null;
        const hasAngles = (context?.angles?.length ?? 0) > 0;
        setProductContext(context);
        setProductContextStatus(hasAngles ? "ready" : "fallback");
      } catch {
        if (!cancelled) {
          setProductContext(null);
          setProductContextStatus("fallback");
        }
      }
    };

    loadContext();

    return () => {
      cancelled = true;
    };
  }, [product.product_id, product.website_id]);

  const handleSelectStyle = (style: VideoStyle) => {
    setSelectedStyle(style);
    setSelectedAvatar(null);
    setSelectedAngle(null);
  };

  const handleContinueStyle = () => {
    if (!selectedStyle) return;

    if (MODELING_STYLE_IDS.has(selectedStyle.id)) {
      setStage("angle");
      return;
    }

    setStage("configure");
  };

  const handleClose = () => {
    if (draftMode) resetDraft();
    onClose();
  };

  const handleSubmittedToGallery = () => {
    // Once the preview has been approved, the gallery in-flight store owns
    // the long render. Release this modal-scoped draft so the user can start
    // another video while the previous one is still being created.
    resetDraft();
    onClose();
  };

  const handleGenerate = async (partialPayload: VideoGenerationRequest) => {
    setIsGenerating(true);

    // ── New draft preview flow (Phase 2) ──
    if (draftEnabled && selectedStyle) {
      await startDraft({
        product_id: product.product_id,
        style_id: selectedStyle.id,
        duration: parseInt(partialPayload.duration || "30"),
        language: partialPayload.language || "es",
        sale_angle_name: partialPayload.angle,
        sale_angle_description: partialPayload.angle_description,
        user_instruction: partialPayload.user_prompt,
        // Phase 6 — UGC + Modeling: forward avatar_config to backend.
        avatar_config: partialPayload.avatar_config ?? null,
        // Phase 2 V2 — voice_config drives TTS generation for modeling
        // voice-over. The sidebar builds this object (type + voice +
        // speed/stability); the backend reads type to decide whether to
        // run ElevenLabs or leave the draft silent.
        voice_config: partialPayload.voice_config ?? null,
        // Phase 2 V3 — the new canonical audio-mode switch (professional,
        // own, fast). Backend prefers this over voice_config.type when
        // both are present. See VideoDraftService.parseAudioMode.
        audio_mode: partialPayload.audio_mode ?? null,
        // Phase 2 V2 — pass the already-rendered portrait so the backend
        // skips the Gemini Image regeneration step. Huge latency + cost
        // win + keeps the exact portrait the user saw in the wizard.
        pre_generated_base_image_url: selectedAvatar?.portraitUrl ?? null,
      });
      setIsGenerating(false);
      // draftMode is now derived from storeStatus — no need to set it manually.
      // When startDraft succeeds, storeStatus transitions to "directing" → draftMode = true.
      return;
    }

    // ── Legacy flow (untouched) ──
    const success = await startVideoGeneration(
      {
        product_id: product.product_id,
        video_content_type: partialPayload.style_id || "UGC",
        style_id: partialPayload.style_id,
        language: partialPayload.language || "es",
        duration: parseInt(partialPayload.duration || "10"),
        tone: partialPayload.tone || undefined,
        instruction: partialPayload.user_prompt,
        angle: partialPayload.angle,
        angle_description: partialPayload.angle_description,
        problem_hint: partialPayload.problem_hint,
        avatar_config: partialPayload.avatar_config ? {
          gender: partialPayload.avatar_config.gender,
          age_range: partialPayload.avatar_config.age_range,
          skin_tone: partialPayload.avatar_config.skin_tone,
          hair: partialPayload.avatar_config.hair,
          vibe: partialPayload.avatar_config.vibe,
          setting: partialPayload.avatar_config.setting,
        } : undefined,
      },
      product.name
    );

    setIsGenerating(false);
    if (success) onClose();
  };

  const handleStartModelingDraft = async (avatar: AvatarCommit) => {
    if (!selectedStyle || !selectedAngle) return;

    setSelectedAvatar(avatar);
    setIsGenerating(true);

    await startDraft({
      product_id: product.product_id,
      style_id: selectedStyle.id,
      duration: 30,
      language: "es",
      sale_angle_name: selectedAngle.name,
      sale_angle_description: selectedAngle.description,
      user_instruction: "",
      avatar_config: avatar.config ?? null,
      voice_config: {
        type: "tts",
        voice_id: "21m00Tcm4TlvDq8ikWAM",
        voice_name: "Sofía",
        speed: 1,
        stability: 0.5,
        language_code: "es",
      },
      audio_mode: AUDIO_MODE.PROFESSIONAL,
      pre_generated_base_image_url: avatar.portraitUrl ?? null,
    });

    setIsGenerating(false);
  };

  // Phase 2: when draftMode is true the user already triggered the new draft
  // flow and should see the preview/approval screen instead of the studio.
  if (draftMode) {
    return (
      <PreviewApproval
        onBack={() => {
          resetDraft();
          // storeStatus goes to "idle" → draftMode becomes false automatically.
        }}
        onDone={handleClose}
        onSubmitted={handleSubmittedToGallery}
      />
    );
  }

  if (stage === "avatar" && selectedStyle && selectedAngle) {
    return (
      <div className="flex flex-col h-full max-h-full overflow-hidden relative bg-white">
        <AvatarWizardModal
          productId={product.product_id}
          websiteId={product.website_id ?? productContext?.website_id ?? null}
          productName={product.name}
          productDescription={product.description}
          productImageUrl={product.image_url ?? null}
          selectedAngle={selectedAngle}
          onBack={() => setStage("angle")}
          onClose={handleClose}
          onCommit={handleStartModelingDraft}
        />
      </div>
    );
  }

  if (stage === "style") {
    return (
      <StudioShell
        product={product}
        onClose={handleClose}
        onBack={handleClose}
        progressStep={1}
      >
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="min-h-0 flex-1 bg-white">
            <VideoStudioGallery
              selectedStyleId={selectedStyle?.id ?? null}
              onSelectStyle={handleSelectStyle}
              onContinue={handleContinueStyle}
              canContinue={!!selectedStyle}
            />
          </div>
        </div>
      </StudioShell>
    );
  }

  if (stage === "angle" && selectedStyle) {
    return (
      <StudioShell
        product={product}
        onClose={handleClose}
        onBack={() => setStage("style")}
        progressStep={2}
      >
        <VideoAngleStep
          product={product}
          productContext={productContext}
          productContextStatus={productContextStatus}
          initialSelection={selectedAngle}
          onBack={() => setStage("style")}
          onContinue={(angle) => {
            setSelectedAngle(angle);
            setStage("avatar");
          }}
        />
      </StudioShell>
    );
  }

  // Phase 2 V2 — Avatar Wizard takes over the entire studio panel visually
  // when active. BUT we can't swap the JSX tree (early-return the wizard
  // instead of the studio) because that unmounts VideoStudioSidebar and
  // loses all its local state — audioMode, voice selections, uploaded
  // audio URL, etc. The user could open the wizard to pick an avatar,
  // come back, and silently lose the sidebar state.
  //
  // Fix (Phase 2 V3): always render the studio tree, and overlay the wizard
  // on top with `display:none` toggling the studio visibility. That keeps
  // the sidebar mounted, so its state survives wizard open/close cycles.
  return (
    <div className="flex flex-col h-full max-h-full overflow-hidden relative">
      {avatarWizardOpen && (
        <div className="absolute inset-0 z-50 bg-white">
          <AvatarWizardModal
            productId={product.product_id}
            websiteId={product.website_id ?? productContext?.website_id ?? null}
            productName={product.name}
            productDescription={product.description}
            productImageUrl={product.image_url ?? null}
            selectedAngle={selectedAngle}
            onBack={() => setAvatarWizardOpen(false)}
            onClose={handleClose}
            onCommit={(avatar) => {
              setSelectedAvatar(avatar);
              setAvatarWizardOpen(false);
            }}
          />
        </div>
      )}
      <div
        className="flex flex-col h-full max-h-full overflow-hidden"
        style={avatarWizardOpen ? { display: "none" } : undefined}
      >
      {/* Header — subtle indigo-tinted gradient so the "tapa" reads as
          branded without drowning the content below. */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-indigo-100/60 flex-shrink-0 bg-gradient-to-r from-indigo-50/50 via-white to-white">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 flex-shrink-0 overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-indigo-200/80">
            {product.image_url ? (
              <img
                src={product.image_url}
                alt={product.name}
                className="h-full w-full object-contain p-0.5"
              />
            ) : (
              <div className="h-full w-full bg-muted/30" />
            )}
          </div>
          <div className="min-w-0">
            <h2 className="max-w-[280px] truncate text-base font-semibold leading-tight text-foreground">
              {shortProductName(product.name)}
            </h2>
          </div>
        </div>
        <button
          type="button"
          onClick={handleClose}
          className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/80 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Main content — fixed 60/40 split. The format gallery stays stable:
          selecting a format only marks its card and updates the right-side
          configuration panel. It should not expand the selected card into a
          large preview, because that makes the layout jump and hides the rest
          of the available formats. */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <div className="w-[60%] min-w-0 min-h-0 overflow-hidden flex-none">
          <VideoStudioGallery
            selectedStyleId={selectedStyle?.id ?? null}
            onSelectStyle={handleSelectStyle}
          />
        </div>

        <VideoStudioSidebar
          product={product}
          selectedStyle={selectedStyle}
          onGenerate={handleGenerate}
          isGenerating={isGenerating}
          avatar={selectedAvatar}
          onOpenAvatarWizard={() => setAvatarWizardOpen(true)}
        />
      </div>
      </div>
    </div>
  );
}

function StudioShell({
  product,
  onClose,
  onBack,
  progressStep,
  children,
}: {
  product: ProductInfoCreative;
  onClose: () => void;
  onBack?: () => void;
  progressStep: number;
  children: ReactNode;
}) {
  return (
    <div className="flex h-full max-h-[90vh] flex-col overflow-hidden bg-white">
      <div className="flex flex-shrink-0 items-center justify-between gap-3 bg-gradient-to-r from-indigo-50/50 via-white to-white px-5 py-2.5">
        <div className="flex min-w-0 items-center gap-2.5">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="mr-0.5 rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-white/80 hover:text-foreground"
              aria-label={progressStep === 1 ? "Volver a seleccionar producto" : "Volver"}
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
          ) : null}
          <div className="h-8 w-8 flex-shrink-0 overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-indigo-200/80">
            {product.image_url ? (
              <img
                src={product.image_url}
                alt={product.name}
                className="h-full w-full object-contain p-0.5"
              />
            ) : (
              <div className="h-full w-full bg-muted/30" />
            )}
          </div>
          <div className="min-w-0">
            <p className="max-w-[260px] truncate text-base font-semibold leading-tight text-foreground">
              {shortProductName(product.name)}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-white/80 hover:text-foreground"
          aria-label="Cerrar"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {children}
    </div>
  );
}

function normalizeAngleTitle(name: string, description?: string | null) {
  const trimmedName = name.trim();
  const trimmedDescription = description?.trim();
  if (!trimmedName && trimmedDescription) {
    return firstReadableAngleClause(trimmedDescription);
  }
  if ((trimmedName.includes("...") || trimmedName.includes("…") || endsWithDanglingAngleWord(trimmedName)) && trimmedDescription) {
    return firstReadableAngleClause(trimmedDescription);
  }
  return trimmedName;
}

function firstReadableAngleClause(value: string) {
  const normalized = value.trim().replace(/\s+/g, " ");
  const clauses = normalized.split(/[;:—–.]/).map((part) => part.trim()).filter(Boolean);
  return clauses.find((clause) => clause.length >= 18 && !endsWithDanglingAngleWord(clause)) ?? normalized.replace(/\.$/, "");
}

function endsWithDanglingAngleWord(value: string) {
  return /\b(a|al|ante|bajo|con|contra|de|del|desde|en|entre|hacia|hasta|la|las|lo|los|para|por|según|sin|sobre|tras|un|una|unos|unas|y|o)$/i.test(
    value.trim(),
  );
}

function compactAngleTitle(title: string) {
  const normalized = title.trim().replace(/\s+/g, " ");
  const primaryClause = normalized.split(/[;—–.]/)[0].trim() || normalized;
  const withoutParentheses = primaryClause.replace(/\s*\([^)]*\)/g, "").replace(/\s+/g, " ").trim();
  const cleaned = withoutParentheses
    .replace(/^Señales de que tienes\s+/i, "Señales de ")
    .replace(/\bCandida\b/g, "cándida")
    .replace(/Picazón cuero cabelludo/gi, "picazón en cuero cabelludo")
    .replace(/cosas blancas en las uñas/gi, "uñas blancas")
    .replace(/presión de repente/gi, "presión repentina")
    .replace(/\.$/, "");

  const parts = cleaned.split(",").map((part) => part.trim()).filter(Boolean);
  if (parts.length >= 3) {
    return `${parts[0]}, ${parts[1]} y ${parts[2]}`;
  }

  if (cleaned.length <= 86) return cleaned;
  const cut = cleaned.slice(0, 86).trimEnd();
  const lastSpace = cut.lastIndexOf(" ");
  return lastSpace >= 56 ? cut.slice(0, lastSpace) : cut;
}

function VideoAngleStep({
  product,
  productContext,
  productContextStatus,
  initialSelection,
  onBack,
  onContinue,
}: {
  product: ProductInfoCreative;
  productContext: VideoProductContextResponse | null;
  productContextStatus: ProductContextStatus;
  initialSelection: SelectedAngle | null;
  onBack: () => void;
  onContinue: (angle: SelectedAngle) => void;
}) {
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [customOpen, setCustomOpen] = useState(false);
  const [customAngle, setCustomAngle] = useState(initialSelection?.name ?? "");

  const angleOptions = useMemo<VideoAngleOption[]>(() => {
    const fallbackOptions = buildVideoAngleOptions(product.name, product.description);

    if (productContextStatus === "ready" && productContext?.angles?.length) {
      const contextOptions = productContext.angles.map((item, index) => {
        const description = item.description || item.name;
        return {
          id: item.id || `context-angle-${index}`,
          name: normalizeAngleTitle(item.name, description),
          description,
          proof: item.proof ?? undefined,
          badge: item.badge ?? (index === 0 ? "Base del website" : "Variación"),
          source: item.source ?? undefined,
        };
      });

      if (contextOptions.length) return contextOptions.slice(0, 4);
    }

    return fallbackOptions;
  }, [product.description, product.name, productContext, productContextStatus]);

  useEffect(() => {
    if (productContextStatus === "loading") return;
    if (!angleOptions.length || selectedOptionId || customOpen) return;

    const match = initialSelection
      ? angleOptions.find((option) => option.name === initialSelection.name)
      : null;
    setSelectedOptionId(match?.id ?? angleOptions[0].id);
  }, [angleOptions, customOpen, initialSelection, productContextStatus, selectedOptionId]);

  const resolvedSelectedOptionId =
    selectedOptionId ?? (!customOpen ? angleOptions[0]?.id ?? null : null);

  const selectedOption = useMemo(
    () => angleOptions.find((option) => option.id === resolvedSelectedOptionId) ?? null,
    [angleOptions, resolvedSelectedOptionId]
  );

  const selectedAngle =
    customOpen && customAngle.trim()
      ? {
          name: customAngle.trim(),
          description: customAngle.trim(),
        }
      : selectedOption
        ? {
            name: selectedOption.name,
            description: selectedOption.description || selectedOption.name,
          }
        : null;
  const shouldCenterAngleRows =
    productContextStatus !== "loading" && !customOpen && angleOptions.length > 0 && angleOptions.length <= 3;

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-slate-50/70">
      <div className="min-h-0 flex-1 overflow-hidden px-6 pb-2 pt-4">
        <div className="mx-auto max-w-[680px]">
          <div className="mb-2 min-w-0">
            <h3 className="text-base font-semibold text-slate-950">
              Elige el ángulo del anuncio
            </h3>
          </div>

          {productContextStatus === "loading" ? <VideoAngleLoading productName={product.name} /> : null}

          {productContextStatus !== "loading" ? (
            <div
              className={cn(
                "space-y-2 transition-all",
                shouldCenterAngleRows ? "flex min-h-[315px] flex-col justify-center" : ""
              )}
            >
              {angleOptions.map((option) => {
                const selected = resolvedSelectedOptionId === option.id && !customOpen;
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => {
                      setCustomOpen(false);
                      setSelectedOptionId(option.id);
                    }}
                    className={cn(
                      "min-h-[50px] w-full rounded-xl border bg-white px-3 py-2 text-left shadow-sm transition-all",
                      selected
                        ? "border-fluxi/35 bg-white shadow-md shadow-indigo-100/50"
                        : "border-slate-200 hover:border-indigo-200 hover:bg-white"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={cn(
                          "flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full",
                          selected ? "bg-indigo-50 text-fluxi" : "bg-slate-100 text-muted-foreground"
                        )}
                      >
                        <Target className="h-3.5 w-3.5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex min-w-0 items-start gap-2">
                              <p className="line-clamp-2 min-w-0 flex-1 text-[13px] font-semibold leading-snug text-slate-950">
                                {compactAngleTitle(option.name)}
                              </p>
                            </div>
                          </div>
                          {selected ? (
                            <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
                              <Check className="h-3.5 w-3.5" />
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}

              <button
                type="button"
                onClick={() => {
                  setCustomOpen(true);
                  setSelectedOptionId(null);
                }}
                className={cn(
                  "w-full rounded-xl border border-dashed px-3 py-2 text-left transition-all",
                  customOpen
                    ? "border-slate-300 bg-white shadow-sm"
                    : "border-slate-200 bg-slate-50/50 text-slate-500 hover:border-slate-300 hover:bg-white"
                )}
              >
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full",
                    customOpen ? "bg-slate-100 text-slate-600" : "bg-slate-100 text-slate-400"
                  )}>
                    <PenLine className="h-3.5 w-3.5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className={cn(
                        "text-[13px] font-semibold",
                        customOpen ? "text-slate-900" : "text-slate-500"
                      )}>
                        Crear ángulo propio
                      </p>
                    </div>
                    {customOpen ? (
                      <textarea
                        value={customAngle}
                        onChange={(event) => setCustomAngle(event.target.value)}
                        placeholder="Ej: Para personas que viven inflamadas después de comer y quieren una rutina simple."
                        className="mt-2 min-h-[72px] w-full resize-none rounded-xl border border-indigo-100 bg-slate-50 px-3 py-2 text-sm leading-relaxed text-slate-900 outline-none transition focus:border-fluxi focus:bg-white"
                        autoFocus
                      />
                    ) : null}
                  </div>
                </div>
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex flex-shrink-0 border-t border-indigo-100/70 bg-white px-6 py-1.5">
        <div className="mx-auto flex w-full max-w-[680px] items-center justify-between gap-3">
          <button
            type="button"
            onClick={onBack}
            className="rounded-xl px-4 py-2 text-sm font-medium text-muted-foreground transition hover:bg-slate-100 hover:text-slate-900"
          >
            Volver
          </button>
          <button
            type="button"
            disabled={!selectedAngle}
            onClick={() => selectedAngle && onContinue(selectedAngle)}
            className={cn(
              "min-w-[170px] rounded-xl px-5 py-1.5 text-sm font-semibold text-white transition",
              selectedAngle
                ? "bg-fluxi shadow-lg shadow-indigo-200 hover:bg-fluxi/90"
                : "cursor-not-allowed bg-indigo-200"
            )}
          >
            Continuar
          </button>
        </div>
      </div>
    </div>
  );
}

function VideoAngleLoading({ productName }: { productName: string }) {
  const [activeStep, setActiveStep] = useState(0);
  const steps = [
    {
      icon: Sparkles,
      title: "Leyendo el website",
    },
    {
      icon: Target,
      title: "Detectando el dolor",
    },
    {
      icon: BarChart3,
      title: "Creando variaciones",
    },
  ];

  useEffect(() => {
    const interval = window.setInterval(() => {
      setActiveStep((current) => (current + 1) % steps.length);
    }, 2200);

    return () => window.clearInterval(interval);
  }, [steps.length]);

  return (
    <div className="flex min-h-[260px] items-center justify-center">
      <div className="w-full max-w-[500px] rounded-2xl border border-indigo-100 bg-white px-4 py-3.5 shadow-[0_14px_42px_rgba(67,104,229,0.10)]">
        <div className="mx-auto flex max-w-lg items-center gap-3">
          <div className="relative flex h-10 w-10 flex-shrink-0 items-center justify-center">
            <div className="absolute inset-0 rounded-full bg-indigo-100/70" />
            <div className="absolute inset-1.5 rounded-full border-[3px] border-indigo-100" />
            <div className="absolute inset-1.5 rounded-full border-[3px] border-transparent border-t-fluxi animate-spin" />
            <div className="relative flex h-7 w-7 items-center justify-center rounded-full bg-fluxi text-white shadow-lg shadow-indigo-200">
              <Sparkles className="h-3.5 w-3.5" />
            </div>
          </div>

          <div className="min-w-0 text-left">
            <h4 className="text-sm font-semibold leading-tight text-slate-950">
              Buscando el mejor ángulo
            </h4>
            <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
              Analizando <span className="font-medium text-slate-700">{shortProductName(productName) || productName}</span>.
            </p>
          </div>
        </div>

        <div className="mt-2.5 space-y-1.5">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = index === activeStep;
            const isDone = index < activeStep;

            return (
              <div
                key={step.title}
                className={cn(
                  "flex min-h-[38px] items-center gap-2.5 rounded-xl border px-3 py-1.5 text-left transition-all duration-300",
                  isActive
                    ? "border-fluxi/35 bg-indigo-50/80 shadow-sm"
                    : isDone
                      ? "border-emerald-100 bg-emerald-50/45"
                      : "border-slate-100 bg-slate-50/70"
                )}
              >
                <div
                  className={cn(
                    "flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full transition-colors",
                    isActive
                      ? "bg-fluxi text-white"
                      : isDone
                        ? "bg-emerald-500 text-white"
                        : "bg-white text-slate-400"
                  )}
                >
                  {isActive ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : isDone ? (
                    <Check className="h-3 w-3" />
                  ) : (
                    <Icon className="h-3 w-3" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[12px] font-semibold leading-tight text-slate-950">{step.title}</p>
                </div>
                {isActive ? (
                  <span className="hidden rounded-full bg-white px-2.5 py-0.5 text-[10px] font-semibold text-fluxi ring-1 ring-indigo-100 sm:inline-flex">
                    En proceso
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
